import {useCallback, useEffect, useRef, useState} from 'react';
import {
    Backdrop,
    Box,
    LinearProgress,
    Paper,
    Portal,
    Stack,
    Typography,
} from '@mui/material';

const OCR_STEP_LABELS = [
    'PDF Recognizing',
    'Data organizing',
    'Repayment Calculating',
    'Data merging',
] as const;

const DOTS_SEQUENCE = ['.', '..', '...'] as const;
const DOTS_INTERVAL_MS = 450;

const LAST_STEP_INDEX = OCR_STEP_LABELS.length - 1;
const LAST_STEP_STALL_PERCENT = 99;

const MIN_STEP_MS = 15_000;
const MAX_STEP_MS = 25_000;

function randomStepDurationMs(): number {
    return MIN_STEP_MS + Math.random() * (MAX_STEP_MS - MIN_STEP_MS);
}

export interface OcrUploadProcessingProps {
    /** Whether the processing overlay is visible */
    open: boolean;
    /** Parent signals that backend data has been received */
    processingComplete?: boolean;
    /** Called when the last step reaches 100% after processingComplete is true */
    onFinished?: () => void;
    /**
     * IR56B page-by-page progress. When pageCount > 0, the overlay shows
     * real “Reading page x of y” instead of loan fake steps.
     */
    variant?: 'loan' | 'ir56b';
    currentPage?: number;
    pageCount?: number;
    namedCount?: number;
}

export function OcrUploadProcessing({
    open,
    processingComplete = false,
    onFinished,
    variant = 'loan',
    currentPage = 0,
    pageCount = 0,
    namedCount = 0,
}: OcrUploadProcessingProps) {
    const [activeStep, setActiveStep] = useState(0);
    const [currentProgress, setCurrentProgress] = useState(0);
    const [dotsIndex, setDotsIndex] = useState(0);

    const rafRef = useRef<number | null>(null);
    const stepStartRef = useRef<number>(0);
    const stepDurationRef = useRef<number>(randomStepDurationMs());
    const activeStepRef = useRef(0);
    const finishedRef = useRef(false);
    const processingCompleteRef = useRef(processingComplete);
    const onFinishedRef = useRef(onFinished);

    useEffect(() => {
        onFinishedRef.current = onFinished;
    }, [onFinished]);

    useEffect(() => {
        processingCompleteRef.current = processingComplete;
    }, [processingComplete]);

    const finishProcessing = useCallback(() => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        onFinishedRef.current?.();
    }, []);

    const resetProgress = useCallback(() => {
        activeStepRef.current = 0;
        stepStartRef.current = 0;
        stepDurationRef.current = randomStepDurationMs();
        finishedRef.current = false;
        setActiveStep(0);
        setCurrentProgress(0);
        setDotsIndex(0);
    }, []);

    useEffect(() => {
        if (!open) return;

        const intervalId = window.setInterval(() => {
            setDotsIndex((prev) => (prev + 1) % DOTS_SEQUENCE.length);
        }, DOTS_INTERVAL_MS);

        return () => window.clearInterval(intervalId);
    }, [open]);

    useEffect(() => {
        if (!open) {
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
            return;
        }

        if (variant === 'ir56b') {
            return;
        }

        resetProgress();
        stepStartRef.current = performance.now();

        const tick = (now: number) => {
            if (finishedRef.current) return;

            const stepIndex = activeStepRef.current;
            const elapsed = now - stepStartRef.current;
            const duration = stepDurationRef.current;
            const isLastStep = stepIndex === LAST_STEP_INDEX;

            let progress: number;

            if (isLastStep) {
                const rawProgress = (elapsed / duration) * LAST_STEP_STALL_PERCENT;
                if (processingCompleteRef.current) {
                    progress = 100;
                } else {
                    progress = Math.min(LAST_STEP_STALL_PERCENT, rawProgress);
                }
            } else {
                progress = Math.min(100, (elapsed / duration) * 100);
            }

            setActiveStep(stepIndex);
            setCurrentProgress(progress);

            if (!isLastStep && progress >= 100) {
                activeStepRef.current = stepIndex + 1;
                stepStartRef.current = now;
                stepDurationRef.current = randomStepDurationMs();
            } else if (isLastStep && progress >= 100 && processingCompleteRef.current) {
                finishProcessing();
                return;
            }

            rafRef.current = requestAnimationFrame(tick);
        };

        rafRef.current = requestAnimationFrame(tick);

        return () => {
            if (rafRef.current != null) {
                cancelAnimationFrame(rafRef.current);
                rafRef.current = null;
            }
        };
    }, [open, variant, resetProgress, finishProcessing]);

    const pageMode = variant === 'ir56b';
    const pagePercent = pageCount > 0
        ? Math.min(100, Math.round((Math.max(currentPage, 0) / pageCount) * 100))
        : 0;
    const displayPercent = pageMode ? pagePercent : Math.round(currentProgress);
    const pageLabel = pageCount > 0
        ? (currentPage > 0
            ? `Reading page ${currentPage} of ${pageCount}`
            : `Preparing ${pageCount} page${pageCount === 1 ? '' : 's'}`)
        : 'Uploading document';

    if (!open) return null;

    return (
        <Portal>
            <Backdrop
                open
                sx={(theme) => ({
                    zIndex: theme.zIndex.modal + 20,
                    color: '#fff',
                    flexDirection: 'column',
                })}
            >
                <Paper
                    elevation={8}
                    sx={{
                        width: 'min(520px, calc(100vw - 48px))',
                        p: 3,
                        borderRadius: 2,
                        pointerEvents: 'auto',
                    }}
                    onClick={(event) => event.stopPropagation()}
                >
                    <Typography variant="h6" sx={{mb: 0.5, fontWeight: 600}}>
                        {pageMode ? 'Reading IR56B' : 'Processing uploaded document'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{mb: 2.5}}>
                        {pageMode
                            ? 'Each page is recognized one by one. Scanned forms can take about 30–60 seconds per page.'
                            : 'Please wait while we recognize and calculate your loan data.'}
                    </Typography>

                    <Box>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{mb: 0.75}}
                        >
                            <Typography variant="body2" sx={{fontWeight: 600}} component="span">
                                {pageMode ? pageLabel : OCR_STEP_LABELS[activeStep]}
                                <Box
                                    component="span"
                                    sx={{
                                        display: 'inline-block',
                                        minWidth: '1.25em',
                                        textAlign: 'left',
                                    }}
                                >
                                    {DOTS_SEQUENCE[dotsIndex]}
                                </Box>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {pageMode && pageCount <= 0 ? '' : `${displayPercent}%`}
                            </Typography>
                        </Stack>
                        <LinearProgress
                            variant={pageMode && pageCount <= 0 ? 'indeterminate' : 'determinate'}
                            value={pageMode ? pagePercent : currentProgress}
                            sx={{
                                height: 8,
                                borderRadius: 4,
                                bgcolor: 'action.hover',
                                '& .MuiLinearProgress-bar': {
                                    borderRadius: 4,
                                    transition: pageMode ? 'transform 0.4s ease' : 'none',
                                },
                            }}
                        />
                        {pageMode ? (
                            <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: 'block'}}>
                                {namedCount > 0
                                    ? `${namedCount} employee${namedCount === 1 ? '' : 's'} recognized so far`
                                    : 'Waiting for the first employee page'}
                            </Typography>
                        ) : null}
                    </Box>
                </Paper>
            </Backdrop>
        </Portal>
    );
}
